import { useEffect, useState } from "react";
import { z } from "zod";
import { fetchFastApi } from "../api/fastapi";
import {
  OptroWorkpaperFile,
  OptroWorkpaperFileSchema,
} from "../models/audit/optroSync";

const WorkStepWorkpapersResponseSchema = z.object({
  workpapers: z.array(OptroWorkpaperFileSchema),
});

// Lists the workpaper candidates hanging off one Optro `ops_audit_item` — its
// files and attachments. Returns the same `OptroWorkpaperFile` shape as
// `useOptroLinkedWorkpapers`, because the backend serves both from one
// `WorkpaperCandidate` model.
//
// Keyed by the numeric `opsAuditItemId` rather than the `WS#<id>` external ref:
// the route takes an int path param so a literal `#` never has to survive
// percent-encoding.
//
// An empty list is the expected answer for most work steps (files exist on 48
// of 1,954 items in the live tenant), so callers must render `[]` as a normal
// state, not as a failed load.
export async function fetchOptroWorkStepWorkpapers(
  opsAuditItemId: number,
): Promise<OptroWorkpaperFile[]> {
  const response = await fetchFastApi({
    method: "GET",
    path: `/work-steps/external/work-step/${opsAuditItemId}/workpapers`,
  });
  return WorkStepWorkpapersResponseSchema.parse(response).workpapers;
}

// `fetchOptroWorkStepWorkpapers` as component state, for the work-step Change
// Workpaper dialog: fetched while `enabled`, cleared otherwise.
export function useOptroWorkStepWorkpapers(
  opsAuditItemId: number | null,
  enabled: boolean,
): { workpapers: OptroWorkpaperFile[]; loading: boolean } {
  const [workpapers, setWorkpapers] = useState<OptroWorkpaperFile[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || opsAuditItemId === null) {
      setWorkpapers([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchOptroWorkStepWorkpapers(opsAuditItemId)
      .then((files) => {
        if (!cancelled) setWorkpapers(files);
      })
      .catch((error) => {
        console.error("Failed to load work step workpapers:", error);
        if (!cancelled) setWorkpapers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, opsAuditItemId]);

  return { workpapers, loading };
}
